import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import {
  ArrowLeft,
  Clock,
  ChevronLeft,
  ChevronRight,
  Save,
  Loader2,
  Check,
  Trash2
} from 'lucide-react';

interface ExtraHour {
  id?: string;
  work_date: string;
  hours: number;
  note: string;
}

export default function SecretariatHours() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [entries, setEntries] = useState<Record<string, ExtraHour>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Calculer les vendredis du mois
  const getFridays = useCallback((): string[] => {
    const fridays: string[] = [];
    const d = new Date(year, month, 1);
    while (d.getMonth() === month) {
      if (d.getDay() === 5) { // vendredi
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        fridays.push(dateStr);
      }
      d.setDate(d.getDate() + 1);
    }
    return fridays;
  }, [year, month]);

  // Charger les données
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const { data } = await supabase
        .from('secretariat_extra_hours')
        .select('id, work_date, hours, note')
        .eq('employee_id', user.id)
        .gte('work_date', startDate)
        .lte('work_date', endDate);

      const map: Record<string, ExtraHour> = {};
      (data || []).forEach((row: { id: string; work_date: string; hours: number; note: string | null }) => {
        map[row.work_date] = { id: row.id, work_date: row.work_date, hours: row.hours, note: row.note || '' };
      });
      setEntries(map);
      setLoading(false);
    };
    load();
  }, [user, year, month]);

  const toggleFriday = (dateStr: string) => {
    setEntries(prev => {
      const copy = { ...prev };
      if (copy[dateStr]) {
        delete copy[dateStr];
      } else {
        copy[dateStr] = { work_date: dateStr, hours: 8, note: '' };
      }
      return copy;
    });
    setSaved(false);
  };

  const updateHours = (dateStr: string, hours: number) => {
    setEntries(prev => ({
      ...prev,
      [dateStr]: { ...prev[dateStr], hours: Math.max(0, Math.min(12, hours)) }
    }));
    setSaved(false);
  };

  const updateNote = (dateStr: string, note: string) => {
    setEntries(prev => ({
      ...prev,
      [dateStr]: { ...prev[dateStr], note }
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    try {
      const fridays = getFridays();

      // Supprimer les entrées décochées
      const toDelete = fridays.filter(f => !entries[f]);
      for (const dateStr of toDelete) {
        const { error } = await supabase
          .from('secretariat_extra_hours')
          .delete()
          .eq('employee_id', user.id)
          .eq('work_date', dateStr);
        if (error) throw error;
      }

      // Upsert les entrées cochées
      const toUpsert = Object.values(entries).map(e => ({
        employee_id: user.id,
        work_date: e.work_date,
        hours: e.hours,
        note: e.note || null
      }));

      if (toUpsert.length > 0) {
        const { error } = await supabase
          .from('secretariat_extra_hours')
          .upsert(toUpsert, { onConflict: 'employee_id,work_date' });
        if (error) throw error;
      }

      toast.success('Heures enregistrées.');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Erreur sauvegarde heures:', err);
      toast.error('Erreur lors de la sauvegarde des heures.');
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const fridays = getFridays();
  const totalHours = Object.values(entries).reduce((sum, e) => sum + e.hours, 0);

  const monthLabel = new Date(year, month).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: '700px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px'
      }}>
        <button
          onClick={() => navigate('/secretariat')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '40px', height: '40px', borderRadius: '10px',
            border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} color="#64748b" />
        </button>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>
            Mes Heures
          </h1>
          <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>
            Déclaration des vendredis travaillés
          </p>
        </div>
      </div>

      {/* Sélecteur de mois */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '12px 20px', marginBottom: '20px'
      }}>
        <button onClick={prevMonth} style={{
          border: 'none', background: '#f1f5f9', borderRadius: '8px',
          padding: '8px', cursor: 'pointer', display: 'flex'
        }}>
          <ChevronLeft size={20} color="#475569" />
        </button>
        <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', textTransform: 'capitalize' }}>
          {monthLabel}
        </span>
        <button onClick={nextMonth} style={{
          border: 'none', background: '#f1f5f9', borderRadius: '8px',
          padding: '8px', cursor: 'pointer', display: 'flex'
        }}>
          <ChevronRight size={20} color="#475569" />
        </button>
      </div>

      {/* Liste des vendredis */}
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
        overflow: 'hidden', marginBottom: '20px'
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>
            Vendredis du mois
          </span>
          <span style={{
            background: totalHours > 0 ? '#fef3c7' : '#f1f5f9',
            color: totalHours > 0 ? '#b45309' : '#64748b',
            padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600'
          }}>
            {totalHours > 0 ? `${totalHours}h déclarées` : 'Aucune heure'}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : fridays.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
            Aucun vendredi ce mois
          </div>
        ) : (
          fridays.map(dateStr => {
            const isActive = !!entries[dateStr];
            const entry = entries[dateStr];
            const [y, m, dd] = dateStr.split('-').map(Number);
            const label = new Date(y, m - 1, dd).toLocaleDateString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long'
            });

            return (
              <div key={dateStr} style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                background: isActive ? '#fffbeb' : 'white',
                transition: 'background 0.2s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Toggle */}
                  <button
                    onClick={() => toggleFriday(dateStr)}
                    style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      border: isActive ? 'none' : '2px solid #cbd5e1',
                      background: isActive ? 'linear-gradient(135deg, #f59e0b, #ea580c)' : 'white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, transition: 'all 0.2s'
                    }}
                  >
                    {isActive ? <Check size={18} color="white" /> : null}
                  </button>

                  {/* Date */}
                  <span style={{
                    flex: 1, fontWeight: '500', color: isActive ? '#92400e' : '#475569',
                    fontSize: '0.95rem', textTransform: 'capitalize'
                  }}>
                    {label}
                  </span>

                  {/* Heures */}
                  {isActive && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="number"
                        value={entry.hours}
                        onChange={e => updateHours(dateStr, parseFloat(e.target.value) || 0)}
                        min={0} max={12} step={0.5}
                        style={{
                          width: '60px', padding: '6px 8px', borderRadius: '8px',
                          border: '1px solid #e2e8f0', textAlign: 'center',
                          fontSize: '0.9rem', fontWeight: '600'
                        }}
                      />
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>h</span>
                    </div>
                  )}

                  {/* Supprimer */}
                  {isActive && (
                    <button
                      onClick={() => toggleFriday(dateStr)}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        padding: '4px', display: 'flex'
                      }}
                    >
                      <Trash2 size={16} color="#ef4444" />
                    </button>
                  )}
                </div>

                {/* Note (si actif) */}
                {isActive && (
                  <input
                    type="text"
                    placeholder="Note (optionnel)"
                    value={entry.note}
                    onChange={e => updateNote(dateStr, e.target.value)}
                    style={{
                      marginTop: '10px', marginLeft: '48px', width: 'calc(100% - 48px)',
                      padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                      fontSize: '0.85rem', color: '#475569'
                    }}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Bouton sauvegarder */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
          background: saved
            ? 'linear-gradient(135deg, #10b981, #059669)'
            : 'linear-gradient(135deg, #f59e0b, #ea580c)',
          color: 'white', fontSize: '1rem', fontWeight: '600',
          cursor: saving ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          opacity: saving ? 0.7 : 1, transition: 'all 0.2s'
        }}
      >
        {saving ? (
          <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Enregistrement...</>
        ) : saved ? (
          <><Check size={18} /> Enregistré</>
        ) : (
          <><Save size={18} /> Enregistrer</>
        )}
      </button>

      {/* Résumé */}
      <div style={{
        marginTop: '20px', background: '#f8fafc', borderRadius: '12px',
        padding: '16px 20px', border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Clock size={18} color="#64748b" />
          <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>Récapitulatif</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
          <div style={{ color: '#64748b' }}>Base mensuelle :</div>
          <div style={{ fontWeight: '600', color: '#1e293b' }}>
            {(() => {
              // Compter les semaines ISO qui chevauchent le mois
              const weeks = new Set<string>();
              const d = new Date(year, month, 1);
              while (d.getMonth() === month) {
                if (d.getDay() >= 1 && d.getDay() <= 4) {
                  const jan4 = new Date(d.getFullYear(), 0, 4);
                  const dayOfWeek = jan4.getDay() || 7;
                  const diff = Math.floor((d.getTime() - jan4.getTime()) / 86400000);
                  const wn = Math.ceil((diff + dayOfWeek) / 7);
                  weeks.add(`${d.getFullYear()}-W${wn}`);
                }
                d.setDate(d.getDate() + 1);
              }
              return `${weeks.size * 32}h (${weeks.size} semaines × 32h)`;
            })()}
          </div>
          <div style={{ color: '#64748b' }}>Vendredis déclarés :</div>
          <div style={{ fontWeight: '600', color: totalHours > 0 ? '#b45309' : '#1e293b' }}>
            {Object.keys(entries).length} vendredi(s) — {totalHours}h
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
